// src/pages/admin/Notificacoes.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import NotificationCard from '../../components/NotificationCard';
import {
  getNotificacoes,
  marcarComoVisualizada,
} from '../../services/notificacoesService';
import {
  aceitarSociedade,
  rejeitarSociedade,
} from '../../services/sociosService';
import {
  aceitarParceria,
  rejeitarParceria,
} from '../../services/parceriasService';

const TABS = [
  { key: 'todas', label: 'Todas' },
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'lidas', label: 'Lidas' },
];

function isActionableType(tipo) {
  // compatibilidade com legado "conexao"
  return ['sociedade', 'parceria', 'conexao'].includes(tipo);
}

function normalizeTipoParceria(tipo) {
  return tipo === 'conexao' ? 'parceria' : tipo;
}

function parseDate(anyDate) {
  // aceita string, number, Date, ou Firestore Timestamp-like { seconds }
  if (!anyDate) return new Date(0);
  if (typeof anyDate?.seconds === 'number')
    return new Date(anyDate.seconds * 1000);
  if (typeof anyDate === 'string' || typeof anyDate === 'number')
    return new Date(anyDate);
  if (anyDate instanceof Date) return anyDate;
  return new Date(0);
}

export default function Notificacoes() {
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('todas');
  const [q, setQ] = useState('');
  const [busyIds, setBusyIds] = useState({}); // loading por item

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setErr('');
      const data = await getNotificacoes();
      // normaliza + ordena decrescente pela data
      const norm = (data || []).map((n) => ({
        ...n,
        tipo: normalizeTipoParceria(n?.tipo),
        _date: parseDate(n?.data),
      }));
      norm.sort((a, b) => b._date - a._date);
      setNotificacoes(norm);
    } catch (e) {
      console.error(e);
      setErr('Não foi possível carregar suas notificações.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    let arr = [...notificacoes];
    if (tab === 'pendentes') {
      arr = arr.filter(
        (n) => !n.visualizado && isActionableType(n.tipo) && n.refId
      );
    } else if (tab === 'lidas') {
      arr = arr.filter((n) => n.visualizado);
    }
    if (q?.trim()) {
      const qq = q.trim().toLowerCase();
      arr = arr.filter(
        (n) =>
          (n.mensagem || '').toLowerCase().includes(qq) ||
          (n.tipo || '').toLowerCase().includes(qq)
      );
    }
    return arr;
  }, [notificacoes, tab, q]);

  const marcarLida = async (id) => {
    try {
      setBusyIds((s) => ({ ...s, [id]: true }));
      await marcarComoVisualizada(id);
      setNotificacoes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, visualizado: true } : n))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setBusyIds((s) => ({ ...s, [id]: false }));
    }
  };

  const handleAceitar = async (item) => {
    if (!item?.refId) return;
    try {
      setBusyIds((s) => ({ ...s, [item.id]: true }));
      if (item.tipo === 'sociedade') {
        await aceitarSociedade(item.refId);
      } else if (item.tipo === 'parceria') {
        await aceitarParceria(item.refId);
      }
      await marcarComoVisualizada(item.id);
      setNotificacoes((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, visualizado: true } : n))
      );
    } catch (err) {
      console.error('Erro ao aceitar:', err);
    } finally {
      setBusyIds((s) => ({ ...s, [item.id]: false }));
    }
  };

  const handleRejeitar = async (item) => {
    if (!item?.refId) return;
    try {
      setBusyIds((s) => ({ ...s, [item.id]: true }));
      if (item.tipo === 'sociedade') {
        await rejeitarSociedade(item.refId);
      } else if (item.tipo === 'parceria') {
        await rejeitarParceria(item.refId);
      }
      await marcarComoVisualizada(item.id);
      setNotificacoes((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, visualizado: true } : n))
      );
    } catch (err) {
      console.error('Erro ao rejeitar:', err);
    } finally {
      setBusyIds((s) => ({ ...s, [item.id]: false }));
    }
  };

  // mapeia link para eventos de imóvel
  const computeLink = (item) => {
    if (!item) return undefined;
    const tiposImovel = ['novo-imovel', 'venda-imovel', 'valor-atualizado'];
    if (tiposImovel.includes(item.tipo) && item.imovelId) {
      return `/imovel/${item.imovelId}`; // mantém seu padrão de rota
    }
    return undefined;
  };

  const avatarForTipo = (tipo) => {
    switch (tipo) {
      case 'sociedade':
        return '/imagens/avatars/avatar-sociedade.jpg';
      case 'parceria':
        return '/imagens/avatars/avatar-conexao.jpg';
      case 'novo-imovel':
      case 'venda-imovel':
      case 'valor-atualizado':
        return '/imagens/avatars/avatar-imovel.jpg';
      default:
        return '/imagens/avatars/avatar-padrao.jpg';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h1 className="text-2xl font-bold">Notificações</h1>

        <div className="flex items-center gap-2">
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Buscar por texto ou tipo..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            onClick={fetchData}
            className="px-3 py-2 text-sm bg-white border rounded hover:bg-gray-50"
          >
            Recarregar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded text-sm border ${
              tab === t.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Status de erro / loading / vazio */}
      {err && (
        <div className="p-3 mb-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {err}
        </div>
      )}
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse bg-gray-100 rounded border"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500">Nenhuma notificação para mostrar.</p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((item) => (
            <li key={item.id}>
              <NotificationCard
                tipo={item.tipo}
                mensagem={item.mensagem}
                time={item._date}
                read={!!item.visualizado}
                isPending={
                  !item.visualizado &&
                  isActionableType(item.tipo) &&
                  !!item.refId
                }
                onAccept={() => handleAceitar(item)}
                onReject={() => handleRejeitar(item)}
                onMarkRead={() => marcarLida(item.id)}
                loading={!!busyIds[item.id]}
                link={computeLink(item)}
                // 👉 prioriza a foto de quem executou a ação; fallback por tipo
                avatarUrl={item.actorFotoUrl || avatarForTipo(item.tipo)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
